<template>
  <v-card>
    <v-card-title class="primary white--text">
      <v-row>
        <v-col>
          <span class="headline">
            {{ title }}
          </span>
        </v-col>
        <v-col cols="auto">
          <BaseDialogActions hideRefresh hideFullscreen/>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-row>
        <v-col cols="12">
          <v-text-field
            ref="diameterGroupLabel"
            :maxLength="maxLabelLength"
            :rules="[rules.required,
            noDuplicateGroupLabelsFieldRule,
            validLabelLengthFieldRule]"
            :label="$t('label')"
            v-model="diameterGroupBuilder.label">
          </v-text-field>
        </v-col>

        <v-col cols="6">
          <v-text-field
          maxLength="2"
          :label="$t('lowerBound')"
          v-model="diameterGroupBuilder.lowerBound"
          :rules="[rules.required,
          rules.positiveIntegerOrBlank,
          diameterInRangeFieldRule,
          noDuplicateDiameterGroupsFieldRule]">
            <template #append><span class="caption" style="opacity: 0.6">{{ $t('inchesAbbreviated') }}</span></template>
          </v-text-field>
        </v-col>

        <v-col cols="6">
          <v-text-field
          maxLength="2"
          :label="$t('upperBound')"
          v-model="diameterGroupBuilder.upperBound"
          :rules="[rules.required,
          rules.positiveIntegerOrBlank,
          diameterInRangeFieldRule,
          noDuplicateDiameterGroupsFieldRule]">
            <template #append><span class="caption" style="opacity: 0.6">{{ $t('inchesAbbreviated') }}</span></template>
          </v-text-field>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="py-4 mx-0">
      <v-row justify="space-between">
        <v-col cols="auto">
          <v-btn color="primary" outlined @click="handleCancel" tabindex="-1" id="cancel">
            <span>
              {{ $t('cancel') }}
            </span>
          </v-btn>
        </v-col>

        <v-col cols="auto">
          <v-btn color="primary" :disabled="!diameterGroupValid" @click="handleSave" id="save">
            <span>
              {{ $t('save') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import fieldRules from '@/utils/rules.js'
import { mapGetters } from 'vuex/dist/vuex.common.js'

const MIN_DIAMETER = 3
const MAX_DIAMETER = 60
const MAX_LABEL_LENGTH = 32

export default {
  name: 'AccountDiameterGroupForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    diameterGroup: {
      type: Object,
      required: false,
      default: () => ({
        label: '',
        lowerBound: '',
        upperBound: ''
      })
    }
  },

  data: () => ({
    maxLabelLength: MAX_LABEL_LENGTH,
    rules: fieldRules.rules,
    diameterGroupBuilder: {
      label: '',
      lowerBound: '',
      upperBound: ''
    }
  }),

  created () {
    if (this.diameterGroup) this.diameterGroupBuilder = JSON.parse(JSON.stringify(this.diameterGroup))
    document.addEventListener('keyup', this.onKeyup)
  },

  beforeDestroy () {
    document.removeEventListener('keyup', this.onKeyup)
  },

  mounted () {
    this.$nextTick(() => {
      this.focus('diameterGroupLabel')
    })
  },

  computed: {
    ...mapGetters('account-scaling', ['accountDiameterGroups']),

    title () {
      return this.diameterGroupBuilder.diameterGroupId
        ? this.$t('editingDiameterGroup')
        : this.$t('creatingDiameterGroup')
    },

    diameterGroupValid () {
      return this.hasValidLabel &&
      this.hasValidBounds &&
      this.noDuplicateGroupLabels &&
      this.noDuplicateDiameterGroups
    },

    hasValidBounds () {
      const { lowerBound, upperBound } = this.diameterGroupBuilder
      return this.diameterInRange(lowerBound) && this.diameterInRange(upperBound)
    },

    hasValidLabel () {
      const { label } = this.diameterGroupBuilder
      return label?.length <= MAX_LABEL_LENGTH
    },

    noDuplicateGroupLabels () {
      return this.accountDiameterGroups
        .every(diameterGroup => diameterGroup.label !== this.diameterGroupBuilder.label ||
          this.isBeingEdited(diameterGroup))
    },

    noDuplicateDiameterGroups () {
      return this.accountDiameterGroups
        .every(diameterGroup => this.uniqueDiameterGroup(diameterGroup) ||
          this.isBeingEdited(diameterGroup))
    }
  },

  methods: {
    diameterInRange (diameter) {
      return diameter >= MIN_DIAMETER && diameter <= MAX_DIAMETER
    },

    focus (refString) {
      this.$nextTick(_ => {
        this.$refs[refString].focus()
      })
    },

    validLabelLengthFieldRule () {
      return this.hasValidLabel ||
        this.$t('cannotExceedNCharacters', { n: MAX_LABEL_LENGTH })
    },

    noDuplicateGroupLabelsFieldRule () {
      return this.noDuplicateGroupLabels ||
        this.$t('cannotDuplicateLabel')
    },

    noDuplicateDiameterGroupsFieldRule () {
      return this.noDuplicateDiameterGroups ||
        this.$t('cannotDuplicateDiameterGroup')
    },

    uniqueDiameterGroup (group) {
      const { lowerBound, upperBound } = group
      return parseInt(lowerBound) !== parseInt(this.diameterGroupBuilder.lowerBound) ||
        parseInt(upperBound) !== parseInt(this.diameterGroupBuilder.upperBound)
    },

    diameterInRangeFieldRule (diameter) {
      if (parseInt(diameter) > MAX_DIAMETER) return this.$t('mustNotExceedN', { n: MAX_DIAMETER })
      if (parseInt(diameter) < MIN_DIAMETER) return this.$t('mustBeAtLeastN', { n: MIN_DIAMETER })
      return true
    },

    handleSave () {
      if (this.diameterGroupValid) {
        this.$emit('save', this.diameterGroupBuilder)
      } else {
        this.setSnackError(this.$t('diameterGroupNotValid'))
      }
    },

    handleCancel () {
      this.$emit('cancel')
    },

    onKeyup (e) {
      if (e.key.toLowerCase() === 'enter' && e.target?.id !== 'save') {
        this.handleSave()
      }
    },

    isBeingEdited (diameterGroup) {
      return this.diameterGroupBuilder.diameterGroupId === diameterGroup.diameterGroupId
    }
  }
}
</script>
